/**
 * Template Management Service
 * API client for objective template CRUD and approval workflow
 */

import { apiClient } from '../apiClient'

export interface ObjectiveTemplateObjective {
  id?: string
  title: string
  description: string
  kpa?: string
  kpaName?: string
  pillar?: string | { id: number; name: string; color_code?: string; description?: string }
  relatedKPIs?: string[]
  success_criteria?: string
}

export interface ObjectiveTemplate {
  id: number
  name: string
  description: string
  template_type: string
  kpa_id?: number | null
  role_type?: string | null
  functional_area?: string | null
  objective_type?: string | null
  title_template?: string | null
  description_template?: string | null
  success_criteria_template?: string | null
  objectives_data?: {
    objectives: ObjectiveTemplateObjective[]
  } | null
  applicable_roles?: string[] | null
  applicable_departments?: string[] | null
  status: string
  is_active: boolean
  is_published: boolean
  created_by_user_id?: number | null
  approved_by_user_id?: number | null
  approved_at?: string | null
  usage_count: number
  last_used_at?: string | null
  created_at: string
  updated_at: string
  created_by?: string | null
  updated_by?: string | null
}

export interface TemplateCreateData {
  name: string
  description: string
  template_type?: string
  kpa_id?: number
  role_type?: string
  functional_area?: string
  objective_type?: string
  title_template?: string
  description_template?: string
  success_criteria_template?: string
  objectives_data?: {
    objectives: ObjectiveTemplateObjective[]
  }
  applicable_roles?: string[]
  applicable_departments?: string[]
}

export interface TemplateUpdateData {
  name?: string
  description?: string
  template_type?: string
  kpa_id?: number
  role_type?: string
  functional_area?: string
  objective_type?: string
  title_template?: string
  description_template?: string
  success_criteria_template?: string
  objectives_data?: {
    objectives: ObjectiveTemplateObjective[]
  }
  applicable_roles?: string[]
  applicable_departments?: string[]
}

export interface TemplateListParams {
  skip?: number
  limit?: number
  search?: string
  template_type?: string
  status?: string
  role_type?: string
  published_only?: boolean
}

export interface TemplateListResponse {
  items: ObjectiveTemplate[]
  total: number
  skip: number
  limit: number
}

export interface TemplateStatsResponse {
  total_templates: number
  approved_templates: number
  pending_approval: number
  published_templates: number
  most_used_template: string | null
  most_used_count: number
}

export interface TemplateApprovalData {
  notes?: string
  publish_immediately?: boolean
}

class TemplateService {
  private baseURL = '/api/v1/performance/templates'

  /**
   * Create a new template
   */
  async createTemplate(data: TemplateCreateData): Promise<ObjectiveTemplate> {
    const response = await apiClient.post<ObjectiveTemplate>(this.baseURL, data)
    return response.data
  }

  /**
   * List templates with filtering and pagination
   */
  async listTemplates(params: TemplateListParams = {}): Promise<TemplateListResponse> {
    const response = await apiClient.get<TemplateListResponse>(this.baseURL, { params })
    return response.data
  }

  /**
   * Get template statistics
   */
  async getStats(): Promise<TemplateStatsResponse> {
    const response = await apiClient.get<TemplateStatsResponse>(`${this.baseURL}/stats`)
    return response.data
  }

  /**
   * Get a specific template by ID
   */
  async getTemplate(templateId: number): Promise<ObjectiveTemplate> {
    const response = await apiClient.get<ObjectiveTemplate>(`${this.baseURL}/${templateId}`)
    return response.data
  }

  /**
   * Update a template
   */
  async updateTemplate(templateId: number, data: TemplateUpdateData): Promise<ObjectiveTemplate> {
    const response = await apiClient.put<ObjectiveTemplate>(`${this.baseURL}/${templateId}`, data)
    return response.data
  }

  /**
   * Submit template for HR approval
   */
  async submitForApproval(templateId: number): Promise<ObjectiveTemplate> {
    const response = await apiClient.post<ObjectiveTemplate>(`${this.baseURL}/${templateId}/submit-for-approval`)
    return response.data
  }

  /**
   * Approve a template (HR Admin only)
   */
  async approveTemplate(templateId: number, data: TemplateApprovalData = {}): Promise<ObjectiveTemplate> {
    const response = await apiClient.post<ObjectiveTemplate>(`${this.baseURL}/${templateId}/approve`, data)
    return response.data
  }

  /**
   * Reject a template (HR Admin only)
   */
  async rejectTemplate(templateId: number, data: TemplateApprovalData): Promise<ObjectiveTemplate> {
    const response = await apiClient.post<ObjectiveTemplate>(`${this.baseURL}/${templateId}/reject`, data)
    return response.data
  }

  /**
   * Publish a template (HR Admin only)
   */
  async publishTemplate(templateId: number): Promise<ObjectiveTemplate> {
    const response = await apiClient.post<ObjectiveTemplate>(`${this.baseURL}/${templateId}/publish`)
    return response.data
  }

  /**
   * Unpublish a template (HR Admin only)
   */
  async unpublishTemplate(templateId: number): Promise<ObjectiveTemplate> {
    const response = await apiClient.post<ObjectiveTemplate>(`${this.baseURL}/${templateId}/unpublish`)
    return response.data
  }

  /**
   * Delete a template (soft delete, HR Admin only)
   */
  async deleteTemplate(templateId: number): Promise<void> {
    await apiClient.delete(`${this.baseURL}/${templateId}`)
  }

  /**
   * Increment template usage counter
   */
  async incrementUsage(templateId: number): Promise<ObjectiveTemplate> {
    const response = await apiClient.post<ObjectiveTemplate>(`${this.baseURL}/${templateId}/increment-usage`)
    return response.data
  }

  /**
   * Get published templates for employees
   */
  async getPublishedTemplates(params: Omit<TemplateListParams, 'published_only'> = {}): Promise<TemplateListResponse> {
    return this.listTemplates({ ...params, published_only: true })
  }

  /**
   * Get templates pending approval (HR view)
   */
  async getPendingTemplates(params: Omit<TemplateListParams, 'status'> = {}): Promise<TemplateListResponse> {
    return this.listTemplates({ ...params, status: 'pending_approval' })
  }

  /**
   * Get approved templates
   */
  async getApprovedTemplates(params: Omit<TemplateListParams, 'status'> = {}): Promise<TemplateListResponse> {
    return this.listTemplates({ ...params, status: 'approved' })
  }
}

export default new TemplateService()
